import { APIClient } from 'vince-core/api/apiClient';
import { VL_API_BASE_URL } from 'vince-core/constants/backendUrls';
import type { SelectedFile } from 'vince-ui/types/common';

export interface SignedUrlResponse {
    /**
     * The actual filename including prefixes
     */
    filename: string;
    /**
     * ISO Date
     */
    expiresAt: string;
    /**
     * HTTP Method to use when accessing the URL
     */
    method: 'GET' | 'PUT';
    /**
     * Signed URL
     */
    url: string;
    /**
     * Optional headers to include when using `PUT`
     */
    headers?: Record<string, string>;
}

export enum BucketType {
    TENANT = 'tenant',
    WORKFLOW = 'workflow',
    TABLE_LOADER = 'table-loader',
    TEMP = 'temp' // Temp files removed after 3 months
}

export enum SignedUrlDirection {
    DOWNLOAD = 'download',
    UPLOAD = 'upload'
}

export enum WorkflowFileType {
    DESIGN = 'designFile',
    TEMPLATE = 'templateFile'
}

export async function downloadUrl (filename: string, bucketType: BucketType = BucketType.TENANT) {
    const signedUrl = await getSignedUrl(filename, bucketType, SignedUrlDirection.DOWNLOAD);
    return signedUrl.url;
}

export async function deleteFile (filename: string, bucketType: BucketType = BucketType.TENANT) {
    const deleteUrl = `${VL_API_BASE_URL}/v1/files/${bucketType}/management/${filename}`;
    return await APIClient<void>(deleteUrl, { method: 'DELETE' });
}

export async function copyFile (params: { sourceKey: string; destinationKey: string; bucketType?: BucketType }): Promise<{ destinationKey: string }> {
    const url = `${VL_API_BASE_URL}/v1/files/${params.bucketType ?? BucketType.TENANT}/copy`;
    return await APIClient<{ destinationKey: string }>(url, {
        method: 'POST',
        body: JSON.stringify({
            sourceKey: params.sourceKey,
            destinationKey: params.destinationKey
        })
    });
}

export async function uploadFile (props: {
    filename: string;
    bucketType: BucketType;
    file: File | Blob | ArrayBuffer | Uint8Array | string;
    'content-type'?: string;
}): Promise<{ filename: string }> {

    const signedUrl = await getSignedUrl(props.filename, props.bucketType, SignedUrlDirection.UPLOAD);

    let uploadBody = props.file;
    // Uint8Array array convert back to blob before upload
    if (Array.isArray(props.file)) {
        const uint8Array = new Uint8Array(props.file);
        uploadBody = new Blob([uint8Array], {
            type: getContentType(props.filename)
        });
    }

    await $fetch(signedUrl.url, {
        body: uploadBody,
        method: signedUrl.method,
        headers: {
            'Content-Type': getContentType(props.filename)
        }
    });

    return {
        filename: signedUrl.filename
    };
}

export async function uploadWorkflowFile (workflowId: string, file: SelectedFile, type: WorkflowFileType) {
    const filenameBase = file.fileName.toLowerCase().split('.');
    const extension = filenameBase.pop();
    const filename = `${workflowId}/${type}/${slugify(filenameBase.join('.'))}.${extension}`;

    const res = await uploadFile({
        filename,
        bucketType: BucketType.TENANT,
        file: file.binaryContent
    });
    return res.filename;
}

export async function uploadWorkflowTriggerFile (workflowId: string, file: File) {
    const filenameBase = file.name.toLowerCase().split('.');
    const extension = filenameBase.pop();
    const filename = `${workflowId}/triggers/${Date.now()}_${slugify(filenameBase.join('.'))}.${extension}`;

    const res = await uploadFile({
        filename,
        bucketType: BucketType.WORKFLOW,
        file
    });
    return res.filename;
}

export async function getSignedUrl (filename: string, bucket: BucketType = BucketType.TENANT, direction = SignedUrlDirection.DOWNLOAD): Promise<SignedUrlResponse> {
    return await APIClient<SignedUrlResponse>(`${VL_API_BASE_URL}/v1/files/${bucket}/${direction}-url/${filename}`);
}

/**
 * Determines the content type of a file based on its extension.
 *
 * @param {string} filename - The name of the file whose content type is to be determined.
 * @return {string} The content type associated with the file extension, or 'application/octet-stream' if the extension is unknown.
 */
export function getContentType (filename?: string): string {
    const ext = filename?.split('.')?.pop()?.toLowerCase();
    switch (ext) {
    case 'xlsx': return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    case 'xls': return 'application/vnd.ms-excel';
    case 'pdf': return 'application/pdf';
    case 'json': return 'application/json';
    case 'txt': return 'text/plain';
    case 'csv': return 'text/plain';
    default: return 'application/octet-stream';
    }
}

export async function convertToSelectedFile (file: File): Promise<SelectedFile> {
    const arrayBuffer = await file.arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);
    return {
        binaryContent: Array.from(uint8Array),
        fileName: file.name,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified
    } as unknown as SelectedFile;
}
